import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import { Router } from 'react-router-dom';
import { hydrate } from 'react-dom';
import createBrowserHistory from 'history/createBrowserHistory';
import { switchMap } from 'rxjs/operators/switchMap';
import { BehaviorSubject } from 'rxjs';
import { createEpicMiddleware } from 'redux-observable';
import { ThemeProvider } from 'emotion-theming';
import theme from './styles/theme';
import { IS_DEV } from '../config/client.config';
import rootEpic from './epics';
import rootReducer from './reducers';
import App from './app';
import createStore from './store';
import createApolloClient from './graphql/createApolloClient/client';
import BaseUrlContext from './helpers/BaseUrl';
import { appendExternalScripts } from './assets/scripts';

const loadUrl = (href) => {
  console.log('Redirecting to', href); // eslint-disable-line
  window.location.href = href;
};

const init = () => {
  const history = createBrowserHistory();

  const { pendingRequestCounterLink, apolloClient } = createApolloClient({
    history,
  });

  const baseUrl = `${window.location.protocol}//${window.location.hostname}${
    window.location.port && window.location.port.length
      ? `:${window.location.port}`
      : ''
  }`;

  const epicMiddleware = createEpicMiddleware({
    dependencies: {
      apolloClient,
      history,
      loadUrl,
      baseUrl,
    },
  });

  const store = createStore({
    epicMiddleware,
    rootReducer,
  });

  const epic$ = new BehaviorSubject(rootEpic);

  // Every time a new epic is given to epic$ it
  // will unsubscribe from the previous one then
  // call and subscribe to the new one because of
  // how switchMap works
  const hotReloadingEpic = (...args) =>
    epic$.pipe(switchMap((epic) => epic(...args)));

  epicMiddleware.run(hotReloadingEpic);

  pendingRequestCounterLink.connect(store);

  // in development, set up HMR:
  if (IS_DEV && module.hot) {
    // Enable Webpack hot module replacement for reducers
    module.hot.accept(
      './reducers/index',
      () => store.replaceReducer(require('./reducers/index').default) // eslint-disable-line
    );

    module.hot.accept(
      './epics/index',
      () => epic$.next(require('./epics/index').default) // eslint-disable-line
    );

    module.hot.accept(); // eslint-disable-line
  }

  const app = (
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <BaseUrlContext.Provider value={baseUrl}>
            <Router history={history}>
              <App />
            </Router>
          </BaseUrlContext.Provider>
        </ThemeProvider>
      </Provider>
    </ApolloProvider>
  );

  loadableReady(() => {
    const rootElement = document.querySelector('.root-element');

    appendExternalScripts();
    hydrate(app, rootElement);
  });
};

init();
